import type { ReactNode } from "react";
import { z } from "zod";
import type { PatientProfileSectionKey } from "../../api/clinicalWorkspace";
import {
  IconAlert,
  IconClipboard,
  IconFamily,
  IconFlask,
  IconFolder,
  IconHistory,
  IconHome,
  IconMail,
  IconPerson,
  IconPill,
  IconScan,
  IconStethoscope,
  IconSyringe,
} from "../../components/IceBlueGlyphs";

/**
 * The patient profile's section registry, in the owner's order (decision
 * record 2026-09-03). It mirrors the encounter's CONTEXT_SECTIONS shape so
 * adding a section is one entry here. `contractKey` names the profile
 * response key a clinical section reads; identity sections have none.
 * Placeholders ship with the page shell so the structure is fixed before
 * their data arrives (labs, imaging, consults, letters, documents).
 */
export const PROFILE_SECTION_KEYS = [
  "overview",
  "demographics",
  "pmh",
  "familyHistory",
  "socialHistory",
  "allergies",
  "immunizations",
  "medications",
  "labs",
  "imaging",
  "consults",
  "letters",
  "visits",
  "documents",
] as const;

export type ProfileSectionKey = (typeof PROFILE_SECTION_KEYS)[number];
export const ProfileSectionKeySchema = z.enum(PROFILE_SECTION_KEYS);

export interface ProfileSection {
  readonly key: ProfileSectionKey;
  readonly label: string;
  /** One line under the panel title; also the placeholder body. */
  readonly description: string;
  readonly icon: () => ReactNode;
  /** Which profile response key this section reads; absent for identity sections. */
  readonly contractKey?: PatientProfileSectionKey;
  /** Shown to identity-only roles (front desk, clinic admin). */
  readonly identityOnly: boolean;
  /** Renders the placeholder body until its data source lands. */
  readonly placeholder: boolean;
}

export const PROFILE_SECTIONS: readonly ProfileSection[] = [
  {
    key: "overview",
    label: "Overview",
    description: "Summary, continuing care and the clinical record.",
    icon: IconHome,
    identityOnly: true,
    placeholder: false,
  },
  {
    key: "demographics",
    label: "Demographics",
    description: "Identity, contact details, emergency contact and coverage.",
    icon: IconPerson,
    identityOnly: true,
    placeholder: false,
  },
  {
    key: "pmh",
    label: "Past medical history",
    description: "Long-running and major diagnoses, including surgical history.",
    icon: IconClipboard,
    contractKey: "pmh",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "familyHistory",
    label: "Family history",
    description: "What runs in the family, in the physician's words.",
    icon: IconFamily,
    contractKey: "familyHistory",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "socialHistory",
    label: "Social history",
    description: "Tobacco, alcohol, substances, occupation and living situation.",
    icon: IconHome,
    contractKey: "socialHistory",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "allergies",
    label: "Allergies",
    description: "Substances to avoid, with the reaction and its severity.",
    icon: IconAlert,
    contractKey: "allergies",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "immunizations",
    label: "Immunizations",
    description: "Vaccines on record, with dates.",
    icon: IconSyringe,
    contractKey: "immunizations",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "medications",
    label: "Medications",
    description: "Current and stopped medications, and where each came from.",
    icon: IconPill,
    contractKey: "medications",
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "labs",
    label: "Labs",
    description:
      "Results from partner labs, scanned reports and the previous EMR, with a trend per test.",
    icon: IconFlask,
    identityOnly: false,
    placeholder: false,
  },
  {
    key: "imaging",
    label: "Imaging & tests",
    description: "Imaging, pulmonary function, ECG and other test reports will appear here.",
    icon: IconScan,
    identityOnly: false,
    placeholder: true,
  },
  {
    key: "consults",
    label: "Consults",
    description: "Specialist reports and the referrals that produced them will appear here.",
    icon: IconStethoscope,
    identityOnly: false,
    placeholder: true,
  },
  {
    key: "letters",
    label: "Letters",
    description:
      "Sick notes, work forms and other letters written for this patient will appear here.",
    icon: IconMail,
    identityOnly: false,
    placeholder: true,
  },
  {
    key: "visits",
    label: "Visits",
    description: "Every visit on this chart, with the signed note.",
    icon: IconHistory,
    identityOnly: true,
    placeholder: false,
  },
  {
    key: "documents",
    label: "Documents",
    description: "Requisitions, handouts and outside records will appear here.",
    icon: IconFolder,
    identityOnly: true,
    placeholder: true,
  },
];

export function profileSection(key: ProfileSectionKey): ProfileSection {
  const section = PROFILE_SECTIONS.find((candidate) => candidate.key === key);
  if (section === undefined) throw new Error(`Unknown profile section: ${key}`);
  return section;
}
